import { Button, Stack, Typography } from '@mui/material'
import { captureMessage } from '@sentry/react'
import { ReactNode, useEffect, useState } from 'react'
import { ThreeDots } from 'react-loader-spinner'
import { logger } from '../../helpers/logger'
import type { SchoolLoadingErrorCode } from './loadingErrorCodes'

const DEFAULT_ERROR_TIMEOUT_MS = 15000

interface ILoadingSpinner {
    fillViewPortWidth?: boolean
    fillViewPortHeight?: boolean
    width?: string | number
    height?: string | number
    id?: string
    children?: ReactNode
    errorCode?: SchoolLoadingErrorCode
    errorTimeoutMs?: number
}

const LoadingSpinner = ({
    fillViewPortWidth,
    fillViewPortHeight,
    width,
    height,
    id,
    children,
    errorCode,
    errorTimeoutMs = DEFAULT_ERROR_TIMEOUT_MS,
}: ILoadingSpinner) => {
    const [timedOut, setTimedOut] = useState(false)

    useEffect(() => {
        setTimedOut(false)
        if (!errorCode) return

        const timeoutId = window.setTimeout(() => {
            const path = window.location.pathname
            logger.error(`Loading timeout ${errorCode} at ${path}`)
            captureMessage(`Loading timeout: ${errorCode}`, {
                level: 'error',
                tags: {
                    loadingErrorCode: errorCode,
                    path,
                },
            })
            setTimedOut(true)
        }, errorTimeoutMs)

        return () => window.clearTimeout(timeoutId)
    }, [errorCode, errorTimeoutMs])

    const containerStyles = {
        width: fillViewPortWidth ? '100vw' : 'unset',
        height: fillViewPortHeight ? '100vh' : 'unset',
        minHeight: timedOut ? 240 : undefined,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    }

    if (timedOut && errorCode) {
        return (
            <Stack
                id={id}
                role="alert"
                spacing={2}
                sx={{ ...containerStyles, px: 3, textAlign: 'center' }}
            >
                <Typography variant="h5">
                    We couldn&apos;t load this page
                </Typography>
                <Typography>
                    Please refresh the page. If the problem continues, contact
                    Spark support and quote error code{' '}
                    <strong>{errorCode}</strong>.
                </Typography>
                <Button
                    variant="contained"
                    onClick={() => window.location.reload()}
                >
                    Refresh page
                </Button>
            </Stack>
        )
    }

    return (
        <Stack id={id} role="status" aria-label="Loading" sx={containerStyles}>
            <ThreeDots
                color={'rgb(255, 178, 2)'}
                width={width ? width : 300}
                height={height ? height : 300}
            />
            {children}
        </Stack>
    )
}

export default LoadingSpinner
